import { observable } from "mobx";
import { DateTime } from "luxon";

import { EventType } from "@gp/models";
import { NameProvider } from "@gp/utility";
import { EventOffer } from "@gp/offer";
import { BetSlipEvent } from "@api-types/agency/bet-slip/BetSlipEvent";
import { format } from "@utils/format";

export class BetOffer {
	nameProvider: NameProvider;
	bettingOfferId: string;
	bettingOfferKeyId: string;
	bettingTypeId: string;
	bettingTypeTip: string;
	currentResult: any;
	eventId: string;
	id: string;
	isBank: boolean;
	isLive: boolean;
	playerFirstName: string;
	playerId: string | null;
	playerLastName?: string;
	specifier: any;
	sportData: any;
	systemIdentifier: string;
	teamGender: number;
	teamId: string | null;
	teamName?: string;
	value: number;
	displayedAmount: string;
	indicator: number;
	isMulti: boolean;
	multiEventName: string;
	submitCount?: number;
	multiOffers?: BetOffer[];
	sportId?: string;

	@observable bettingOfferStatus: number;

	constructor(betOffer: BetSlipEvent) {
		this.nameProvider = new NameProvider();

		this.bettingOfferId = betOffer.bettingOfferId;
		this.bettingOfferKeyId = betOffer.bettingOfferKeyId;
		this.bettingOfferStatus = betOffer.bettingOfferStatus;
		this.bettingTypeId = betOffer.bettingTypeId;
		this.bettingTypeTip = betOffer.bettingTypeTip;
		this.currentResult = format.safeJsonParse(betOffer.currentResult, {});
		this.eventId = betOffer.eventId;
		this.id = betOffer.id;
		this.isBank = betOffer.isBank;
		this.isLive = betOffer.isLive;
		this.playerFirstName = betOffer.playerFirstName;
		this.playerId = betOffer.playerId;
		this.playerLastName = betOffer.playerLastName;
		this.specifier = betOffer.specifier;
		this.sportData = betOffer.sportData || {};
		this.sportData.result = format.safeJsonParse(this.sportData.result, {});
		this.systemIdentifier = betOffer.systemIdentifier;
		this.teamGender = betOffer.teamGender || 0;
		this.teamId = betOffer.teamId;
		this.teamName = betOffer.teamName;
		this.value = betOffer.value;

		if (Array.isArray(betOffer.multiOffers)) {
			this.multiOffers = betOffer.multiOffers.map(
				(multiOfferData) => new BetOffer(multiOfferData)
			);
			this.applyTeamNamesToMultiOffers();
		}
	}

	applyTeamNamesToMultiOffers() {
		if (this.multiOffers) {
			this.multiOffers.forEach((multiOffer) => {
				if (!multiOffer.sportData.teamOneName) {
					multiOffer.sportData.teamOneName =
						this.sportData.teamOneName;
				}
				if (!multiOffer.sportData.teamTwoName) {
					multiOffer.sportData.teamTwoName =
						this.sportData.teamTwoName;
				}
			});
		}
	}

	//#region getters

	get formattedBettingOfferStatus() {
		return format.formatBettingOfferStatus(this.bettingOfferStatus);
	}

	get timeString() {
		if (this.sportData.eventStartTime) {
			return DateTime.fromISO(this.sportData.eventStartTime).toFormat(
				"dd.MM. HH:mm"
			);
		}
	}

	get emptyForMultiTimeString() {
		if (this.hasOwnProperty("isMulti") && this.isMulti == true) {
			return "";
		}

		return this.timeString;
	}

	get teamOneString() {
		return format.formatTeamName(
			this.sportData.teamOneName,
			this.sportData.teamOneGender,
			this.sportData.eventType
		);
	}

	get teamTwoString() {
		return format.formatTeamName(
			this.sportData.teamTwoName,
			this.sportData.teamTwoGender,
			this.sportData.eventType
		);
	}

	scorePerPeriodString(liveResult?: EventOffer | null) {
		const sportData = liveResult || this.sportData;
		if (sportData.result?.scorePerPeriod == null) {
			return "";
		}

		const periodScores = sportData.result.scorePerPeriod
			.reduce((acc: string[], value: { home: number; away: number }) => {
				acc.push(`${value.home || 0}:${value.away || 0}`);
				return acc;
			}, [])
			.join("; ");

		return `(${periodScores})`;
	}

	get scorePerPeriodAtBetTimeString() {
		if (this.currentResult?.scorePerPeriod == null) {
			return "";
		}

		return this.currentResult.scorePerPeriod
			.reduce((acc: string[], value: { home: number; away: number }) => {
				acc.push(`(${value.home || 0}:${value.away || 0})`);
				return acc;
			}, [])
			.join(" ");
	}

	formattedCurrentResult(liveResult: EventOffer | null) {
		const sportData = liveResult || this.sportData;

		if (this.sportData.sportAbrv == "cricket") {
			const spp = sportData?.result?.scorePerPeriod;

			if (spp) {
				if (spp.length > 1) {
					return `${spp[0].home || 0}/${spp[1].home || 0}:${spp[0].away || 0}/${spp[1].away || 0}`;
				} else {
					return `${spp[0].home || 0}:${spp[0].away || 0}`;
				}
			} else {
				return "0:0";
			}
		}

		if (sportData?.result?.fullTimeScore != null) {
			return `${sportData.result.fullTimeScore.home || 0}:${sportData.result.fullTimeScore.away || 0}`;
		}

		if (sportData?.result?.currentScore != null) {
			return `${sportData.result.currentScore.home || 0}:${sportData.result.currentScore.away || 0}`;
		}

		return "";
	}

	get formattedResultAtBetTime() {
		if (this.currentResult?.currentScore != null) {
			return `${this.currentResult.currentScore.home || 0}:${
				this.currentResult.currentScore.away || 0
			}`;
		}

		return "";
	}

	get formattedBettingType() {
		const bettingType = this.nameProvider.getName(
			this.sportData.bettingTypeNameForBettingSlip,
			this.formattedOfferSpecifier
		);
		return bettingType;
	}

	get formattedBettingTypeTip() {
		if (
			this.sportData.eventType != undefined &&
			this.sportData.eventType != EventType.NORMAL
		) {
			if (this.playerId != null) {
				return `${this.playerLastName}, ${this.playerFirstName}`;
			} else if (this.teamId != null) {
				return this.teamName;
			}
			return this.bettingTypeTip;
		}
		const bettingTypeTip = this.nameProvider.getName(
			this.bettingTypeTip,
			this.formattedOfferSpecifier
		);
		return bettingTypeTip;
	}

	get formattedOfferSpecifier() {
		return {
			competitor1: this.sportData.teamOneName,
			competitor2: this.sportData.teamTwoName,
			...this.specifier,
		};
	}

	//#endregion getters
}
